import { Handle, type NodeProps, Position } from "@xyflow/react";
import { AlertTriangle, Download, Loader2, Trash2 } from "lucide-react";
import { openExternal } from "@/components/app-shell";
import { Button } from "@/components/ui/button";
import { track } from "@/lib/analytics";
import { assetUrl } from "@/lib/assets";
import { GENERATION_NODE_WIDTH, type GenerationNode } from "@/lib/board-graph";
import type { HostedModelInfo } from "@/lib/hosted-models";
import { cn, formatRelativeDate } from "@/lib/utils";
import { type GenerationRow, useBoardData } from "./board-context";

function generationLabel(
	generation: GenerationRow | undefined,
	models: HostedModelInfo[]
): string {
	if (!generation) {
		return "Loading…";
	}
	if (!generation.provider.startsWith("fal:")) {
		return "Generating with Codex…";
	}
	const label = models.find((m) => m.id === generation.model)?.label ?? "Pluck";
	return generation.status === "queued"
		? `Queued for ${label}…`
		: `Generating with ${label}…`;
}

/** "2 references · 20 credits · just now". */
function cardMeta(generation: GenerationRow): string {
	const references =
		generation.referenceAssetIds?.length ?? generation.inputPluckIds.length;
	const parts = [`${references} reference${references === 1 ? "" : "s"}`];
	if (generation.creditCost) {
		parts.push(`${generation.creditCost} credits`);
	}
	parts.push(formatRelativeDate(generation.createdAt));
	return parts.join(" · ");
}

function GenerationBody({
	generation,
	hostedModels,
}: {
	generation: GenerationRow | undefined;
	hostedModels: HostedModelInfo[];
}) {
	if (generation?.status === "succeeded" && generation.outputAssetId) {
		return (
			<img
				alt="Generated design"
				className="max-h-[520px] w-full object-contain"
				src={assetUrl(generation.outputAssetId)}
			/>
		);
	}
	if (generation?.status === "failed") {
		return (
			<div className="flex flex-col items-center gap-2 p-6 text-center">
				<AlertTriangle className="size-5 text-destructive" />
				<p className="font-medium text-xs">Generation failed</p>
				<p className="max-w-xs break-words text-[11px] text-muted-foreground">
					{generation.error ?? "Unknown error"}
				</p>
			</div>
		);
	}
	return (
		<div className="flex flex-col items-center gap-2 p-6 text-muted-foreground">
			<Loader2 className="size-5 animate-spin" />
			<p className="text-xs">{generationLabel(generation, hostedModels)}</p>
		</div>
	);
}

export function GenerationNodeView({
	id,
	data,
	selected,
}: NodeProps<GenerationNode>) {
	const { generationsById, onRemoveNode, hostedModels } = useBoardData();
	const generation = generationsById.get(data.generationId);

	return (
		<div
			className={cn(
				"group flex flex-col overflow-hidden rounded-xl border bg-card shadow-sm",
				selected && "ring-2 ring-ring"
			)}
			style={{ width: GENERATION_NODE_WIDTH }}
		>
			<Handle position={Position.Left} type="target" />
			{/* Feeds this image into another prompt as a reference. */}
			<Handle position={Position.Right} type="source" />
			<div className="flex min-h-48 items-center justify-center bg-muted/40">
				<GenerationBody generation={generation} hostedModels={hostedModels} />
			</div>
			<div className="flex items-center gap-2 border-t px-3 py-2">
				<div className="min-w-0 flex-1">
					<p className="truncate font-medium text-xs">Generation</p>
					{generation && (
						<p className="truncate text-[11px] text-muted-foreground">
							{cardMeta(generation)}
						</p>
					)}
				</div>
				<Button
					aria-label="Delete generation"
					className="opacity-0 group-hover:opacity-100"
					onClick={() => onRemoveNode(id)}
					size="icon-sm"
					title="Delete generation"
					variant="ghost"
				>
					<Trash2 />
				</Button>
				{generation?.outputAssetId && (
					<Button
						aria-label="Open full size"
						onClick={() => {
							if (generation.outputAssetId) {
								track("generation.opened_full_size", {
									runner: generation.provider.startsWith("fal:")
										? "hosted"
										: "local",
								});
								openExternal(assetUrl(generation.outputAssetId));
							}
						}}
						size="icon-sm"
						variant="ghost"
					>
						<Download />
					</Button>
				)}
			</div>
		</div>
	);
}
